import {
  useState, useRef, useEffect,
} from 'react';
import {
  Stepper,
  DetailList,
  Button,
  Form,
} from '@skyline-platform/alchemy';
import { StreamForm } from '../StreamForm/StreamForm';
import { SearchExistingSource } from '../SourceForm/SearchExistingSource';
import PropTypes from 'prop-types';
import './StreamStepperFlow.css';
import '../FullScreenForm.css';
import '@styles/base.css';
import { saveStream, updateStream } from '@utils/graphql/creators/streams';
import { getUpdates } from './utils';
import { useEffectOnce } from '@utils/react/hooks';
import { getStreams } from '@utils/graphql/creators/streams';
import { notifications } from '@utils/notifications/index';
import { handleEnterKeyDown } from '../helpers';
import { getLogger } from '@utils/general';
import { getStreamProfileLabel } from '@pages/utils/helpers';

const logger = getLogger('src.components.StreamForm');

export const StreamStepperFlow = ({
  handleClose, streamData, setNewStream, setTitle, lockDownFields,
}) => {
  const [formData, setFormData] = useState({
    stream: {},
    source: {},
    zone: {},
    appliance: {},
    camera: {},
    isValid: false,
  });
  const previousStream = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreamData = async () => {
      if (!streamData) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const data = await retrieveStreamById(streamData);
      if (data) {
        previousStream.current = data;
        setFormData((prevData) => ({
          ...prevData,
          stream: {
            ...data,
            onDemand: data.pull_on_request,
            rtspInterleaved: data.use_interleaved_rtsp_transport,
          },
          source: {
            ...data.source,
            name: getStreamProfileLabel(data.source),
          },
          zone: data.zone,
          appliance: data.appliance,
          camera: {},
          isValid: true,
        }));
      }
      setLoading(false);
    };

    fetchStreamData();
  }, [streamData]);

  const finalFormData = () => {
    const isValid = (obj) => Object.keys(obj).length && obj.id && obj.name;
    if (Object.keys(formData.stream).length
      && isValid(formData.source)
      && isValid(formData.zone)
      && isValid(formData.appliance)) {
      const streamDetails = {
        Name: formData.stream.name,
        Description: formData.stream.description,
        'On Demand': formData.stream.onDemand ? 'On' : 'Off',
        'RTSP Interleaved': formData.stream.rtspInterleaved ? 'On' : 'Off',
        ...(formData.stream.snapshotInterval
          ? { 'Snapshot Interval': `${formData.stream.snapshotInterval} seconds` }
          : {}),
        Zone: formData.zone.name,
        'Host Appliance': formData.appliance.name,
        'Stream Profile': formData.source.name,
      };
      const addition = formData.source.device?.name
        ? { Camera: formData.source.device?.name }
        : { Appliance: formData.source.appliance?.name };
      const streamEntries = Object.entries(streamDetails);
      streamEntries.splice(
        streamEntries.findIndex(([key]) => key === 'Stream Profile') + 1,
        0,
        ...Object.entries(addition)
      );
      return Object.fromEntries(streamEntries);
    } else {
      return {};
    }
  };

  const steps = [
    {
      label: 'Stream',
      component: (
        <StreamForm
          formData={formData}
          setFormData={setFormData}
        />
      ),
    },
    {
      label: 'Stream Profile',
      component: (
        <SearchExistingSource
          formData={formData.source}
          setFormData={(data, isValid) => {
            setFormData((prevData) => ({
              ...prevData,
              source: data,
              isValid: isValid,
            }));
          }}
          lockDownFields={lockDownFields}
        />
      ),
    },
    {
      label: 'DetailList',
      component: <DetailList keyValuePair={finalFormData()} />, // Passing all the collected data to DetailList
    },
  ];

  const handleSave = async () => {
    const inputs = {
      'name': formData.stream.name,
      'description': formData.stream.description,
      'online': true,
      'useInterleavedRTSPTransport': formData.stream.rtspInterleaved,
      'snapshot_interval': formData.stream.snapshotInterval === '' ? null : formData.stream.snapshotInterval,
      'pullOnRequest': formData.stream.onDemand,
      'lowLatencyPullOnRequest': true,
      'sourceId': formData.source.id,
      'applianceId': formData.appliance.id,
      'zoneId': formData.zone.id,
    };
    const { mutation, execute } = saveStream('create_stream', inputs);
    mutation.addReturnProperties('id', 'name');

    const failMessage = `Failed to create stream ${inputs.name}.`;
    try {
      let newStream;
      await execute()
        .then(({ data, error }) => {
          if (data) {
            newStream = data;
          }
          if (error) {
            notifications.error(failMessage);
            logger.error(error, failMessage);
            return;
          }
        });

      if (!newStream) return;
      setNewStream?.({
        id: newStream.id,
        name: newStream.name,
      });
      notifications.success(`Successfully created stream ${inputs.name}.`);
      handleClose();
    }
    catch (error) {
      notifications.error(failMessage);
      logger.error(error, failMessage);
    }
  };

  const handleEdit = async () => {
    const updates = getUpdates(previousStream.current, formData);
    const inputs = {
      ...updates,
    };
    const { mutation, execute } = updateStream('update_stream', previousStream.current.id, inputs);
    mutation.addReturnProperties('id', 'name');

    const failMessage = 'Failed to edit stream.';
    try {
      await execute()
        .then(({ error }) => {
          if (error) {
            notifications.error(failMessage);
            logger.error(error, failMessage);
          }
        });
      notifications.success(`Successfully updated Stream ${formData.stream.name}.`);
      handleClose();
    }
    catch (error) {
      notifications.error(failMessage);
      logger.error(error, failMessage);
    }
  };

  const handleStepChange = (step) => {
    const isLastStep = step === steps.length - 1;
    const title = streamData ? 'Edit Stream' : 'Create New Stream';
    setTitle(!isLastStep ? title : 'Stream Details');
  };

  useEffectOnce(() => handleStepChange(0));

  return (
    loading ? <div>Loading...</div> : // Will be a loading component in the future
      <Form formTitle='Create New Stream' key={formData}>
        <Stepper
          steps={steps}
          showLabel={false}
          hasSummaryStep={false}
          isDisabled={!formData.isValid}
          cancelButton={
            <Button
              onClick={handleClose}
              onKeyDown={(event) => handleEnterKeyDown(event, handleClose)}
              type='outlined'
              label='Cancel'
            />
          }
          finalButton={
            <Button
              variant='contained'
              onClick={streamData ? handleEdit : handleSave}
              onKeyDown={(event) => streamData
                ? handleEnterKeyDown(event, handleEdit)
                : handleEnterKeyDown(event, handleSave)
              }
              type='filled'
              label={streamData ? 'Update Stream' : 'Add Stream'}
            />
          }
          onStepChange={handleStepChange}
        />
      </Form>
  );
};

const retrieveStreamById = async (streamId) => {
  const { query, execute } = getStreams(streamId);
  query.addReturnProperties(
    'id',
    'name',
    'description',
    'pull_on_request',
    'use_interleaved_rtsp_transport',
    'snapshot_interval',
    'appliance.id',
    'appliance.name',
    'source.id',
    'source.name',
    'source.device.id',
    'source.device.name',
    'source.device.network_address',
    'source.appliance.id',
    'source.appliance.name',
    'source.appliance.management_address',
    'zone.id',
    'zone.name'
  );

  try {
    const { error, data } = await execute();
    if (error) {
      logger.error(error);
      return null;
    }
    return data;
  } catch (e) {
    logger.warn(e);
    return null;
  }
};

StreamStepperFlow.propTypes = {
  handleClose: PropTypes.func.isRequired,
  streamData: PropTypes.string, // Probably should be renamed to streamDataId in future
  setNewStream: PropTypes.func,
  setTitle: PropTypes.func,
  lockDownFields: PropTypes.object,
};

